import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Compass, ArrowRight, Pencil, MapPin, MagnifyingGlass } from "@phosphor-icons/react";import { toast } from "sonner";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import SetTrackerDialog from "../components/SetTrackerDialog";

/**
 * In-House Team — Vehicle Tracking overview.
 *
 * Shows every active operative as a card. If they have a tracker URL, the
 * embed previews inline (small height) and the whole card links to the
 * full-screen tracker page. If not, a CTA prompts the admin to paste their
 * iframe snippet from the tracker provider.
 */
export default function TrackingOverviewPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);  // operative being edited

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/in-house/team/trackers");
      setTeam(data.team || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load team trackers");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const filtered = team.filter(t => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (t.name + " " + t.email).toLowerCase().includes(q);
  });

  const withTracker = team.filter(t => t.has_tracker).length;
  const total = team.length;

  return (
    <div className="space-y-6" data-testid="tracking-overview-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-[Chivo] font-bold flex items-center gap-2">
            <Compass className="h-8 w-8 text-blue-600" weight="duotone" />
            Vehicle Tracking
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Live van trackers for every in-house operative. Each card embeds the tracker provider&apos;s own map — click into any card for a full-screen view.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            <strong className="text-foreground">{withTracker}</strong> of <strong className="text-foreground">{total}</strong> operative{total === 1 ? "" : "s"} have a tracker configured.
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="pl-9"
          data-testid="tracking-search-input"
        />
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-10">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-border rounded-lg p-16 text-center" data-testid="tracking-empty">
          <Compass className="h-12 w-12 mx-auto text-muted-foreground" weight="duotone" />
          <p className="mt-4 font-semibold">No in-house operatives found</p>
          <p className="text-sm text-muted-foreground mt-1">Add team members under In-House Team first.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(op => (
            <OperativeTrackerCard
              key={op.user_id}
              op={op}
              onEdit={() => setEditTarget(op)}
            />
          ))}
        </div>
      )}

      <SetTrackerDialog
        operative={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); fetchTeam(); }}
      />
    </div>
  );
}

function OperativeTrackerCard({ op, onEdit }) {
  return (
    <div
      className="border border-border rounded-lg bg-card overflow-hidden flex flex-col"
      data-testid={`tracking-card-${op.user_id}`}
    >
      <div className="p-3 border-b border-border flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold truncate">{op.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">{op.email}</p>
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1 shrink-0"
          data-testid={`tracking-edit-${op.user_id}`}
          title="Set or update tracker URL"
        >
          <Pencil size={12} /> {op.has_tracker ? "Edit" : "Set"}
        </button>
      </div>

      {op.has_tracker ? (
        <>
          <div className="relative bg-muted/30" style={{ aspectRatio: "16 / 9" }}>
            <iframe
              src={op.tracker_embed_url}
              title={`${op.name} tracker preview`}
              className="absolute inset-0 w-full h-full"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              referrerPolicy="no-referrer"
              loading="lazy"
              data-testid={`tracking-iframe-${op.user_id}`}
            />
          </div>
          <Link
            to={`/in-house/tracking/${op.user_id}`}
            className="flex items-center justify-between text-sm px-3 py-2 hover:bg-muted transition-colors border-t border-border"
            data-testid={`tracking-view-${op.user_id}`}
          >
            <span className="flex items-center gap-1.5 text-blue-700 dark:text-blue-400 font-medium">
              <MapPin size={14} /> Full-screen tracker
            </span>
            <ArrowRight size={14} />
          </Link>
        </>
      ) : (
        <div className="p-6 text-center bg-muted/10 flex-1 flex flex-col items-center justify-center gap-3">
          <p className="text-sm text-muted-foreground">No tracker configured yet.</p>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`tracking-empty-cta-${op.user_id}`}>
            <Pencil size={12} className="mr-1" /> Paste iframe snippet
          </Button>
        </div>
      )}
    </div>
  );
}
